import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Briefcase, FileText, Grid3x3, BarChart3, Mail, LogOut, Plus,
  Trash2, X, Pencil, Settings, LineChart, LayoutTemplate, Image, Menu, ExternalLink, Scale,
  Search, Save, ChevronLeft, ChevronRight,
} from "lucide-react";
import Logo from "@/components/Logo";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageUpload from "@/components/admin/ImageUpload";
import Analytics from "@/components/admin/Analytics";
import ContentManager from "@/components/admin/ContentManager";
import Submissions from "@/components/admin/Submissions";
import Overview from "@/components/admin/Overview";
import LegalManager from "@/components/admin/LegalManager";
import InsightEditor from "@/components/admin/InsightEditor";

const NAV_GROUPS = [
  { title: "Insights", tabs: [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "analytics", label: "Analytics", icon: LineChart },
  ] },
  { title: "Content", tabs: [
    { key: "pages", label: "Page Content", icon: LayoutTemplate },
    { key: "site", label: "Site Content", icon: Settings },
    { key: "insights", label: "Insights", icon: FileText },
    { key: "portfolio", label: "Portfolio", icon: Briefcase },
    { key: "media", label: "Media Library", icon: Image },
    { key: "legal", label: "Legal Pages", icon: Scale },
  ] },
  { title: "Audience", tabs: [
    { key: "submissions", label: "Submissions", icon: Inbox },
    { key: "newsletter", label: "Newsletter", icon: Mail },
  ] },
  { title: "Taxonomy", tabs: [
    { key: "sectors", label: "Sectors", icon: Grid3x3 },
    { key: "statistics", label: "Statistics", icon: BarChart3 },
  ] },
];
const ALL_TABS = NAV_GROUPS.flatMap((g) => g.tabs);

const SITE_FIELDS = [
  ["hero_headline", "Hero Headline (English)", "text"], ["hero_headline_ar", "Hero Headline (Arabic)", "text"],
  ["hero_subtext", "Hero Subtext (English)", "textarea"], ["hero_subtext_ar", "Hero Subtext (Arabic)", "textarea"],
  ["about_heading", "Home About Heading (English)", "text"], ["about_heading_ar", "Home About Heading (Arabic)", "text"],
  ["about_body", "Home About Body (English)", "textarea"], ["about_body_ar", "Home About Body (Arabic)", "textarea"],
  ["footer_statement", "Footer Statement (English)", "textarea"], ["footer_statement_ar", "Footer Statement (Arabic)", "textarea"],
  ["linkedin_url", "LinkedIn URL", "text"], ["location", "Location", "text"], ["contact_email", "Contact Email", "text"],
];

const PORTFOLIO_FIELDS = [
  ["name", "Company Name", "text"], ["name_ar", "Company Name (Arabic)", "text"],
  ["sector", "Sector", "text"], ["relationship_type", "Relationship Type", "text"],
  ["status", "Status", "text"], ["location", "Location", "text"], ["year", "Year", "text"],
  ["website", "Website", "text"], ["image_url", "Image", "image"],
  ["description", "Description / Opportunity", "textarea"], ["description_ar", "Description (Arabic)", "textarea"],
  ["overview", "Company Overview", "textarea"], ["overview_ar", "Company Overview (Arabic)", "textarea"],
  ["intersect_role", "Intersect Role", "textarea"], ["intersect_role_ar", "Intersect Role (Arabic)", "textarea"],
];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[80] bg-obsidian/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6">
      <div className="bg-charcoal border border-primary-gold/20 rounded-2xl w-full max-w-2xl my-6">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-white/5">
          <h3 className="text-lg font-light text-ivory">{title}</h3>
          <button onClick={onClose} className="text-muted-text hover:text-ivory p-1" data-testid="modal-close"><X size={20} /></button>
        </div>
        <div className="p-5 sm:p-6">{children}</div>
      </div>
    </div>
  );
}

function EntityForm({ fields, initial, onSave, onCancel, saveLabel = "Save" }) {
  const [data, setData] = useState(initial || {});
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(data); }} className="space-y-5">
      {fields.map(([k, label, type]) => (
        <div key={k}>
          {type === "image" ? (
            <ImageUpload label={label} value={data[k]} onChange={(v) => setData({ ...data, [k]: v })} testid={`field-${k}`} />
          ) : (
            <>
              <label className="eyebrow block mb-2">{label}</label>
              {type === "textarea"
                ? <textarea rows={5} className="field resize-none text-sm" data-testid={`field-${k}`} value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} />
                : <input className="field text-sm" data-testid={`field-${k}`} value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} />}
            </>
          )}
        </div>
      ))}
      <div className="flex flex-wrap gap-3 pt-2">
        <button type="submit" className="btn-solid btn-sm" data-testid="entity-save"><Save size={14} strokeWidth={1.5} /> {saveLabel}</button>
        <button type="button" onClick={onCancel} className="btn-gold btn-sm">Cancel</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("intersect_admin_nav") === "collapsed");
  const [data, setData] = useState({ contacts: [], opportunities: [], newsletter: [], portfolio: [], insights: [], sectors: [], statistics: [], settings: {} });
  const [editing, setEditing] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [contacts, opportunities, newsletter, portfolio, insights, sectors, statistics, settings] = await Promise.all([
        api.get("/admin/contacts"), api.get("/admin/opportunities"), api.get("/admin/newsletter"),
        api.get("/portfolio"), api.get("/admin/insights"), api.get("/sectors"), api.get("/statistics"), api.get("/settings"),
      ]);
      setData({ contacts: contacts.data, opportunities: opportunities.data, newsletter: newsletter.data, portfolio: portfolio.data, insights: insights.data, sectors: sectors.data, statistics: statistics.data, settings: settings.data || {} });
    } catch { toast.error("Failed to load data."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { localStorage.setItem("intersect_admin_nav", collapsed ? "collapsed" : "expanded"); }, [collapsed]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const saveSettings = async (payload) => {
    try { await api.put("/admin/settings", payload); toast.success("Site content saved."); load(); }
    catch { toast.error("Save failed."); }
  };

  const saveEntity = async (kind, payload) => {
    try {
      if (payload.id) await api.put(`/admin/${kind}/${payload.id}`, payload);
      else await api.post(`/admin/${kind}`, payload);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch { toast.error("Save failed."); throw new Error("save failed"); }
  };
  const delEntity = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/${kind}/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };
  const exportCsv = (which) => window.open(`${API}/admin/${which}/export`, "_blank");

  const activeLabel = ALL_TABS.find((t) => t.key === tab)?.label || "Admin";
  const go = (key) => { setTab(key); setNavOpen(false); };
  const pendingCount = data.contacts.filter((c) => !c.archived).length + data.opportunities.filter((o) => !o.archived).length;
  const badges = { submissions: pendingCount };

  const navWidth = collapsed ? "md:w-[76px]" : "md:w-[264px]";

  return (
    <div className="min-h-screen bg-obsidian text-soft-stone">
      {/* Top bar (all breakpoints) */}
      <header className={`sticky top-0 z-30 flex items-center gap-3 border-b border-primary-gold/10 bg-obsidian/95 backdrop-blur px-4 md:px-6 py-3 ${navOpen ? "pointer-events-none md:pointer-events-auto" : ""}`}>
        <button onClick={() => setNavOpen(true)} className="md:hidden text-ivory p-1" data-testid="admin-menu-open" aria-label="Open menu"><Menu size={22} /></button>
        <button onClick={() => setCollapsed((v) => !v)} className="hidden md:inline-flex text-muted-text hover:text-primary-gold p-1" data-testid="admin-nav-collapse" aria-label="Toggle navigation">
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-text">Intersect Admin</p>
          <p className="text-sm text-primary-gold truncate" data-testid="admin-breadcrumb">{activeLabel}</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-2 text-xs text-muted-text hover:text-primary-gold px-3 py-2" data-testid="admin-view-site-desktop">
          <ExternalLink size={14} strokeWidth={1.5} /> View site
        </a>
        <a href="/" className="sm:hidden text-muted-text p-1" data-testid="admin-view-site" aria-label="View site"><ExternalLink size={18} /></a>
        <div className="hidden md:flex items-center gap-3 pl-3 border-l border-white/10">
          <div className="w-8 h-8 rounded-full bg-primary-gold/15 border border-primary-gold/30 flex items-center justify-center text-xs text-primary-gold uppercase">
            {(user?.name || user?.email || "A").slice(0, 1)}
          </div>
          <button onClick={doLogout} className="text-muted-text hover:text-primary-gold p-1" title="Sign out" data-testid="admin-logout-top"><LogOut size={16} strokeWidth={1.5} /></button>
        </div>
      </header>

      <div className="flex">
        {navOpen && <div className="md:hidden fixed inset-0 z-40 bg-obsidian/70 backdrop-blur-sm" onClick={() => setNavOpen(false)} data-testid="admin-nav-overlay" />}

        <aside aria-hidden={!navOpen && window.innerWidth < 768}
          className={`fixed md:sticky top-0 md:top-[61px] left-0 z-50 md:z-0 h-full md:h-[calc(100vh-61px)] w-[272px] max-w-[85vw] ${navWidth} shrink-0 bg-obsidian md:bg-transparent border-r border-primary-gold/10 flex flex-col transition-all duration-300 ${navOpen ? "translate-x-0" : "-translate-x-full pointer-events-none md:pointer-events-auto"} md:translate-x-0`}>
          <div className="flex items-center justify-between p-5 md:hidden">
            <Logo width={140} />
            <button onClick={() => setNavOpen(false)} className="text-muted-text hover:text-ivory" data-testid="admin-menu-close" aria-label="Close menu"><X size={20} /></button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 pb-4 md:pt-5 space-y-5" data-testid="admin-nav">
            {NAV_GROUPS.map((g) => (
              <div key={g.title}>
                {!collapsed && <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.22em] text-muted-text/70">{g.title}</p>}
                <div className="space-y-1">
                  {g.tabs.map((t) => (
                    <button key={t.key} onClick={() => go(t.key)} data-testid={`admin-tab-${t.key}`} title={t.label}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-sm rounded-xl transition-colors ${collapsed ? "md:justify-center" : ""} ${tab === t.key ? "text-primary-gold bg-primary-gold/10 border border-primary-gold/25" : "text-muted-text hover:text-soft-stone hover:bg-white/5 border border-transparent"}`}>
                      <t.icon size={16} strokeWidth={1.5} className="shrink-0" />
                      <span className={collapsed ? "md:hidden" : ""}>{t.label}</span>
                      {badges[t.key] > 0 && (
                        <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full bg-primary-gold/20 text-primary-gold ${collapsed ? "md:hidden" : ""}`}>{badges[t.key]}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="p-4 border-t border-white/5">
            <p className={`text-xs text-muted-text truncate ${collapsed ? "md:hidden" : ""}`}>{user?.email}</p>
            <button onClick={doLogout} className={`mt-3 flex items-center gap-2 text-sm text-muted-text hover:text-primary-gold ${collapsed ? "md:justify-center md:w-full" : ""}`} data-testid="admin-logout">
              <LogOut size={15} strokeWidth={1.5} /><span className={collapsed ? "md:hidden" : ""}>Sign out</span>
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
          {tab === "overview" && <Overview data={data} user={user} setTab={setTab} />}
          {tab === "analytics" && <Analytics />}
          {tab === "pages" && <ContentManager mode="text" />}
          {tab === "media" && <ContentManager mode="media" />}
          {tab === "legal" && <LegalManager />}

          {tab === "site" && (
            <div data-testid="admin-site" className="max-w-2xl">
              <p className="eyebrow mb-1">Content</p>
              <h1 className="text-2xl md:text-3xl font-light text-ivory mb-3">Site Content</h1>
              <p className="text-sm text-muted-text mb-8">Manage the homepage hero, about intro and footer content shown across the website (English &amp; Arabic). Leave a field blank to use the built-in default.</p>
              <EntityForm fields={SITE_FIELDS} initial={data.settings} onSave={saveSettings} onCancel={() => load()} />
            </div>
          )}

          {tab === "submissions" && <Submissions contacts={data.contacts} opportunities={data.opportunities} exportCsv={exportCsv} reload={load} />}

          {tab === "portfolio" && (
            <CrudList title="Portfolio" items={data.portfolio} fields={PORTFOLIO_FIELDS} kind="portfolio"
              searchKeys={["name", "sector", "relationship_type"]}
              editing={editing} setEditing={setEditing} onSave={(p) => saveEntity("portfolio", p)} onDelete={(id) => delEntity("portfolio", id)}
              render={(r) => (
                <div className="min-w-0">
                  <p className="text-ivory text-sm truncate">{r.name}</p>
                  <p className="text-xs text-muted-text truncate">{r.relationship_type} · {r.sector}</p>
                </div>
              )} />
          )}

          {tab === "insights" && (
            <InsightsManager items={data.insights} editing={editing} setEditing={setEditing}
              onSave={(p) => saveEntity("insights", p)} onDelete={(id) => delEntity("insights", id)} />
          )}

          {tab === "sectors" && (
            <CrudList title="Sectors" items={data.sectors} fields={[["name", "Sector Name (English)", "text"], ["name_ar", "Sector Name (Arabic)", "text"]]} kind="sectors"
              searchKeys={["name"]}
              editing={editing} setEditing={setEditing} onSave={(p) => saveEntity("sectors", p)} onDelete={(id) => delEntity("sectors", id)}
              render={(r) => <span className="text-ivory text-sm">{r.name}</span>} />
          )}

          {tab === "statistics" && (
            <CrudList title="Statistics" items={data.statistics} fields={[["value", "Value (e.g. $XXXB)", "text"], ["label", "Label", "text"], ["label_ar", "Label (Arabic)", "text"]]} kind="statistics"
              searchKeys={["label", "value"]}
              editing={editing} setEditing={setEditing} onSave={(p) => saveEntity("statistics", p)} onDelete={(id) => delEntity("statistics", id)}
              render={(r) => (
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="serif-italic text-2xl text-luxury-gold">{r.value}</span>
                  <span className="text-xs text-muted-text truncate">{r.label}</span>
                </div>
              )} />
          )}

          {tab === "newsletter" && (
            <div data-testid="admin-newsletter">
              <p className="eyebrow mb-1">Audience</p>
              <h1 className="text-2xl md:text-3xl font-light text-ivory mb-8">Newsletter Subscribers</h1>
              <div className="space-y-2">
                {data.newsletter.length === 0 && <p className="text-muted-text">No subscribers yet.</p>}
                {data.newsletter.map((n) => (
                  <div key={n.id} className="a-card px-5 py-3 flex flex-wrap justify-between gap-2">
                    <span className="text-soft-stone text-sm break-all">{n.email}</span>
                    <span className="text-xs text-muted-text">{new Date(n.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function CrudList({ title, items, fields, kind, render, editing, setEditing, onSave, onDelete, searchKeys = [] }) {
  const [q, setQ] = useState("");
  const open = editing?.kind === kind;
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) => searchKeys.some((k) => String(r[k] || "").toLowerCase().includes(s)));
  }, [items, q, searchKeys]);

  return (
    <div data-testid={`admin-${kind}`}>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">{items.length} {items.length === 1 ? "entry" : "entries"}</p>
          <h1 className="text-2xl md:text-3xl font-light text-ivory">{title}</h1>
        </div>
        <button onClick={() => setEditing({ kind, data: {} })} className="btn-solid btn-sm" data-testid={`add-${kind}`}><Plus size={14} strokeWidth={1.5} /> Add</button>
      </div>
      {searchKeys.length > 0 && items.length > 4 && (
        <div className="relative mb-5 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-text" />
          <input className="field pl-9 text-sm" placeholder={`Search ${title.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`search-${kind}`} />
        </div>
      )}
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-muted-text text-sm">No entries yet.</p>}
        {filtered.map((r) => (
          <div key={r.id} className="a-card px-4 sm:px-5 py-4 flex items-center justify-between gap-3" data-testid={`${kind}-row`}>
            {render(r)}
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setEditing({ kind, data: r })} className="p-2 text-muted-text hover:text-primary-gold" data-testid={`edit-${kind}`} aria-label="Edit"><Pencil size={15} /></button>
              <button onClick={() => onDelete(r.id)} className="p-2 text-muted-text hover:text-destructive" data-testid={`delete-${kind}`} aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {open && (
        <Modal title={editing.data.id ? `Edit ${title}` : `Add ${title}`} onClose={() => setEditing(null)}>
          <EntityForm fields={fields} initial={editing.data} onSave={onSave} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </div>
  );
}

function statusOf(r) {
  if (!r.published) return { label: "Draft", cls: "text-amber-300" };
  if (r.publish_at && new Date(r.publish_at) > new Date()) return { label: "Scheduled", cls: "text-sky-300" };
  return { label: "Published", cls: "text-emerald-300" };
}

function InsightsManager({ items, editing, setEditing, onSave, onDelete }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const open = editing?.kind === "insights";

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((r) => {
      const st = statusOf(r).label.toLowerCase();
      if (filter !== "all" && st !== filter) return false;
      if (!s) return true;
      return `${r.title} ${r.category} ${r.author}`.toLowerCase().includes(s);
    });
  }, [items, q, filter]);

  return (
    <div data-testid="admin-insights">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">{items.length} articles</p>
          <h1 className="text-2xl md:text-3xl font-light text-ivory">Insights</h1>
        </div>
        <button onClick={() => setEditing({ kind: "insights", data: {} })} className="btn-solid btn-sm" data-testid="add-insights"><Plus size={14} strokeWidth={1.5} /> New Insight</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex gap-2 flex-wrap">
          {["all", "published", "draft", "scheduled"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`insight-filter-${f}`}
              className={`px-4 py-2 text-xs uppercase tracking-wide rounded-full border transition-colors ${filter === f ? "border-primary-gold text-primary-gold bg-primary-gold/10" : "border-white/10 text-muted-text hover:text-soft-stone"}`}>{f}</button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-text" />
          <input className="field pl-9 text-sm" placeholder="Search insights…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-insights" />
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-muted-text text-sm">No insights match this view.</p>}
        {filtered.map((r) => {
          const st = statusOf(r);
          return (
            <div key={r.id} className="a-card px-4 sm:px-5 py-4 flex items-center justify-between gap-3" data-testid="insights-row">
              <button onClick={() => setEditing({ kind: "insights", data: r })} className="min-w-0 text-left flex-1" data-testid="insights-row-open">
                <p className="text-ivory text-sm truncate">{r.title}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`a-chip ${st.cls}`}>{st.label}</span>
                  <span className="text-xs text-muted-text truncate">{r.category} · {r.author}{r.publish_at ? ` · ${new Date(r.publish_at).toLocaleString()}` : ""}</span>
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing({ kind: "insights", data: r })} className="p-2 text-muted-text hover:text-primary-gold" data-testid="edit-insights" aria-label="Edit"><Pencil size={15} /></button>
                <button onClick={() => onDelete(r.id)} className="p-2 text-muted-text hover:text-destructive" data-testid="delete-insights" aria-label="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {open && <InsightEditor initial={editing.data} categories={Array.from(new Set(items.map((i) => i.category)))} onSave={onSave} onClose={() => setEditing(null)} />}
    </div>
  );
}
